import { LitElement, html, nothing } from '../../vendor/lit/lit-core.esm.js?v=20260730a';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { ProjectPersistenceService } from '../project/project_persistence_service.js?v=20260904f';
import { openConfirmDialog } from '../ui/dialog_service.js?v=20260817b';
import {
  fetchMessageJson,
  getMessageRuntimeState,
  subscribeMessages,
  syncUnreadCount
} from './message_runtime.js?v=20260806a';

const MAX_CONTEXTS = 10;

function formatMessageTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

function contextIcon(iconKey) {
  return iconKey === 'folder' ? '▰' : '▣';
}

function messageId(message) {
  return Number(message?.messageId || 0);
}

function conversationId(conversation) {
  return Number(conversation?.conversationId || 0);
}

class KsMessageCenter extends LitElement {
  static properties = {
    conversations: { attribute: false },
    messages: { attribute: false },
    activeConversationId: { attribute: false },
    activeRecipient: { attribute: false },
    recipients: { attribute: false },
    recipientQuery: { attribute: false },
    recipientSearchOpen: { type: Boolean },
    providers: { attribute: false },
    presentation: { attribute: false },
    contextPicker: { attribute: false },
    contextTargets: { attribute: false },
    hasMore: { type: Boolean },
    nextCursor: { attribute: false },
    loading: { type: Boolean },
    loadingOlder: { type: Boolean },
    sending: { type: Boolean },
    connected: { type: Boolean },
    statusMessage: { attribute: false },
    statusError: { type: Boolean },
    mobileConversationOpen: { type: Boolean },
    composerHasContent: { type: Boolean }
  };

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.conversations = [];
    this.messages = [];
    this.activeConversationId = null;
    this.activeRecipient = null;
    this.recipients = [];
    this.recipientQuery = '';
    this.recipientSearchOpen = false;
    this.providers = [];
    this.presentation = null;
    this.contextPicker = null;
    this.contextTargets = [];
    this.hasMore = false;
    this.nextCursor = null;
    this.loading = true;
    this.loadingOlder = false;
    this.sending = false;
    this.connected = getMessageRuntimeState().connected;
    this.statusMessage = '';
    this.statusError = false;
    this.mobileConversationOpen = false;
    this.composerHasContent = false;
    this._unsubscribe = null;
    this._recipientSearchTimer = null;
    this._contextSearchTimer = null;
    this._composerRange = null;
    this._visibilityHandler = () => this._scheduleOpenedCheck();
  }

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribe = subscribeMessages(event => this._handleRuntimeEvent(event));
    document.addEventListener('visibilitychange', this._visibilityHandler);
    this._loadInitial();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    this._unsubscribe = null;
    document.removeEventListener('visibilitychange', this._visibilityHandler);
    clearTimeout(this._recipientSearchTimer);
    clearTimeout(this._contextSearchTimer);
  }

  updated(changed) {
    if (changed.has('messages') || changed.has('activeConversationId') || changed.has('mobileConversationOpen')) {
      this._scheduleOpenedCheck();
    }
  }

  async _loadInitial() {
    this.loading = true;
    try {
      await Promise.all([
        this._loadConversations(),
        this._loadSettings()
      ]);
      this.connected = getMessageRuntimeState().connected;
      this._setStatus('', false);
    } catch (error) {
      this._setStatus(error.message || t('messages.error.load', 'Nachrichten konnten nicht geladen werden.'), true);
    } finally {
      this.loading = false;
    }
  }

  async _loadSettings() {
    const payload = await fetchMessageJson('/api/messages/settings');
    this.providers = Array.isArray(payload.providers) ? payload.providers : [];
    this.presentation = payload.presentation || null;
  }

  async _loadConversations() {
    const payload = await fetchMessageJson('/api/messages/conversations');
    this.conversations = Array.isArray(payload.conversations) ? payload.conversations : [];
    if (this.activeConversationId) {
      const current = this.conversations.find(item => conversationId(item) === Number(this.activeConversationId));
      if (current) this.activeRecipient = current.otherUser;
    }
  }

  _setStatus(message, error = false) {
    this.statusMessage = message || '';
    this.statusError = Boolean(error);
  }

  _styleVariables() {
    const settings = this.presentation || {};
    return [
      `--ks-msg-own-bg:${settings.ownBackground || '#FFE2BF'}`,
      `--ks-msg-own-text:${settings.ownText || '#1F1F1F'}`,
      `--ks-msg-received-bg:${settings.receivedBackground || '#ECEFF2'}`,
      `--ks-msg-received-text:${settings.receivedText || '#1F1F1F'}`,
      `--ks-msg-context-bg:${settings.contextBackground || '#FFF8EF'}`,
      `--ks-msg-context-text:${settings.contextText || '#5D3511'}`,
      `--ks-msg-context-border:${settings.contextBorder || '#E7A963'}`,
      `--ks-msg-check:${settings.openedCheck || '#5D3511'}`,
      `--ks-msg-active:${settings.activeContact || '#F28C28'}`,
      `--ks-msg-radius:${Number(settings.bubbleRadius) || 16}px`,
      `--ks-msg-max-width:${Number(settings.bubbleMaxWidth) || 72}%`,
      `--ks-msg-gap:${Number(settings.messageGap) || 8}px`
    ].join(';');
  }

  async _handleRuntimeEvent(event) {
    if (event.type === 'connection') {
      this.connected = event.connected === true;
      return;
    }
    if (event.type === 'connection-error') {
      this.connected = false;
      return;
    }
    if (event.type === 'reconnect-sync') {
      await this._loadConversations().catch(() => undefined);
      if (this.activeConversationId) {
        await this._refreshOpenStatuses();
        await this._loadConversation(this.activeConversationId, { preserveRecipient: true, silent: true });
      }
      return;
    }
    if (event.type === 'new-message') {
      const incoming = event.message;
      if (incoming && Number(event.conversationId) === Number(this.activeConversationId)) {
        if (!this.messages.some(item => messageId(item) === messageId(incoming))) {
          this.messages = [...this.messages, incoming].sort((a, b) => messageId(a) - messageId(b));
          await this.updateComplete;
          this._scrollToLatest();
          this._scheduleOpenedCheck();
        }
      }
      await this._loadConversations().catch(() => undefined);
      return;
    }
    if (event.type === 'opened' && Number(event.conversationId) === Number(this.activeConversationId)) {
      const openedById = new Map((event.opened || []).map(item => [Number(item.messageId), item.openedAt]));
      this.messages = this.messages.map(message => openedById.has(messageId(message))
        ? { ...message, openedByRecipient: true, openedAt: openedById.get(messageId(message)) }
        : message);
      return;
    }
    if (event.type === 'hidden' && Number(event.conversationId) === Number(this.activeConversationId)) {
      this.messages = this.messages.filter(message => messageId(message) !== Number(event.messageId));
      await this._loadConversations().catch(() => undefined);
      return;
    }
    if (event.type === 'conversation-cleared' && Number(event.conversationId) === Number(this.activeConversationId)) {
      this.messages = [];
      this.activeConversationId = null;
      this.activeRecipient = null;
      this.mobileConversationOpen = false;
      await this._loadConversations().catch(() => undefined);
      return;
    }
    if (event.type === 'settings-updated') {
      await this._loadSettings().catch(() => undefined);
    }
  }

  _openRecipientSearch = () => {
    this.recipientSearchOpen = true;
    this.recipientQuery = '';
    this.recipients = [];
    this._searchRecipients('');
    this.updateComplete.then(() => this.querySelector('.ks-message-recipient-search input')?.focus());
  };

  _closeRecipientSearch = () => {
    this.recipientSearchOpen = false;
    this.recipients = [];
  };

  _onRecipientQuery = event => {
    this.recipientQuery = event.target.value || '';
    clearTimeout(this._recipientSearchTimer);
    this._recipientSearchTimer = setTimeout(() => this._searchRecipients(this.recipientQuery), 250);
  };

  async _searchRecipients(query) {
    try {
      const payload = await fetchMessageJson(`/api/messages/recipients?q=${encodeURIComponent(query || '')}`);
      if (String(query || '') !== String(this.recipientQuery || '')) return;
      this.recipients = Array.isArray(payload.recipients) ? payload.recipients : [];
    } catch (error) {
      this._setStatus(error.message, true);
    }
  }

  _selectRecipient = recipient => {
    const existing = this.conversations.find(item => Number(item.otherUser?.userId) === Number(recipient.userId));
    this.recipientSearchOpen = false;
    this.recipients = [];
    if (existing) {
      this._selectConversation(existing);
      return;
    }
    this.activeConversationId = null;
    this.activeRecipient = recipient;
    this.messages = [];
    this.hasMore = false;
    this.nextCursor = null;
    this.mobileConversationOpen = true;
    this._setStatus('', false);
    this.updateComplete.then(() => this._composerElement()?.focus());
  };

  _selectConversation = async conversation => {
    const id = conversationId(conversation);
    if (!id) return;
    this.activeRecipient = conversation.otherUser;
    this.mobileConversationOpen = true;
    await this._loadConversation(id, { preserveRecipient: true });
  };

  async _loadConversation(id, options = {}) {
    this.loading = options.silent !== true;
    try {
      const payload = await fetchMessageJson(`/api/messages/conversations/${encodeURIComponent(id)}/messages`);
      this.activeConversationId = Number(id);
      this.messages = Array.isArray(payload.messages) ? payload.messages : [];
      this.hasMore = payload.hasMore === true;
      this.nextCursor = payload.nextCursor || null;
      if (!options.preserveRecipient) {
        this.activeRecipient = this.conversations.find(item => conversationId(item) === Number(id))?.otherUser || null;
      }
      await this.updateComplete;
      this._scrollToLatest();
      this._scheduleOpenedCheck();
      this._setStatus('', false);
    } catch (error) {
      this._setStatus(error.message || t('messages.error.conversation', 'Unterhaltung konnte nicht geladen werden.'), true);
    } finally {
      this.loading = false;
    }
  }

  async _loadOlder() {
    if (!this.activeConversationId || !this.hasMore || !this.nextCursor || this.loadingOlder) return;
    this.loadingOlder = true;
    const scroller = this.querySelector('.ks-message-thread');
    const previousHeight = scroller?.scrollHeight || 0;
    try {
      const payload = await fetchMessageJson(`/api/messages/conversations/${this.activeConversationId}/messages?before=${encodeURIComponent(this.nextCursor)}`);
      const known = new Set(this.messages.map(messageId));
      const older = (payload.messages || []).filter(message => !known.has(messageId(message)));
      this.messages = [...older, ...this.messages];
      this.hasMore = payload.hasMore === true;
      this.nextCursor = payload.nextCursor || null;
      await this.updateComplete;
      if (scroller) scroller.scrollTop += scroller.scrollHeight - previousHeight;
      this._scheduleOpenedCheck();
    } catch (error) {
      this._setStatus(error.message, true);
    } finally {
      this.loadingOlder = false;
    }
  }

  _scrollToLatest() {
    const scroller = this.querySelector('.ks-message-thread');
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }

  _scheduleOpenedCheck() {
    requestAnimationFrame(() => requestAnimationFrame(() => this._markVisibleMessagesOpened()));
  }

  async _markVisibleMessagesOpened() {
    if (!this.activeConversationId || document.visibilityState !== 'visible' || !this.isConnected || this.offsetParent === null) return;
    const thread = this.querySelector('.ks-message-thread');
    if (!thread || thread.offsetParent === null) return;
    const threadRect = thread.getBoundingClientRect();
    const ids = this.messages
      .filter(message => !message.own)
      .filter(message => {
        const element = thread.querySelector(`[data-message-id="${messageId(message)}"]`);
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.bottom > threadRect.top && rect.top < threadRect.bottom;
      })
      .map(messageId);
    if (!ids.length) return;
    try {
      await fetchMessageJson(`/api/messages/conversations/${this.activeConversationId}/opened`, {
        method: 'POST',
        body: JSON.stringify({ messageIds: ids })
      });
      await syncUnreadCount();
      await this._loadConversations();
    } catch (_error) {
      // A later scroll/reconnect/visibility transition retries idempotently.
    }
  }

  async _refreshOpenStatuses() {
    const ids = this.messages.filter(message => message.own).map(messageId);
    if (!ids.length) return;
    try {
      const payload = await fetchMessageJson('/api/messages/open-status', {
        method: 'POST',
        body: JSON.stringify({ messageIds: ids })
      });
      const byId = new Map((payload.statuses || []).map(status => [Number(status.messageId), status]));
      this.messages = this.messages.map(message => byId.has(messageId(message)) ? { ...message, ...byId.get(messageId(message)) } : message);
    } catch (_error) {
      // Realtime updates remain an optimization; the next reconnect retries.
    }
  }

  _composerElement() {
    return this.querySelector('.ks-message-composer-editor');
  }

  _rememberComposerSelection = () => {
    const editor = this._composerElement();
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (editor.contains(range.commonAncestorContainer)) this._composerRange = range.cloneRange();
    this._updateSendButton();
  };

  _updateSendButton() {
    this.composerHasContent = this._composerHasContent();
  }

  _composerHasContent() {
    return this._composerParts().some(part => part.type === 'context' || String(part.text || '').trim());
  }

  _appendTextPart(parts, text) {
    if (!text) return;
    const previous = parts.at(-1);
    if (previous?.type === 'text') previous.text += text;
    else parts.push({ type: 'text', text });
  }

  _composerParts() {
    const editor = this._composerElement();
    if (!editor) return [];
    const parts = [];
    const visit = (node, blockDepth = 0) => {
      if (node.nodeType === Node.TEXT_NODE) {
        this._appendTextPart(parts, node.textContent || '');
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const element = node;
      if (element.matches('[data-context-token]')) {
        parts.push({
          type: 'context',
          providerKey: element.dataset.providerKey || '',
          targetId: element.dataset.targetId || ''
        });
        return;
      }
      if (element.tagName === 'BR') {
        this._appendTextPart(parts, '\n');
        return;
      }
      const isBlock = ['DIV', 'P'].includes(element.tagName);
      if (isBlock && blockDepth > 0 && parts.length) this._appendTextPart(parts, '\n');
      [...element.childNodes].forEach(child => visit(child, blockDepth + (isBlock ? 1 : 0)));
    };
    [...editor.childNodes].forEach((node, index) => {
      if (index > 0 && node.nodeType === Node.ELEMENT_NODE && ['DIV', 'P'].includes(node.tagName)) this._appendTextPart(parts, '\n');
      visit(node, 0);
    });
    return parts.filter(part => part.type === 'context' || part.text !== '');
  }

  _onComposerPaste = event => {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    document.execCommand('insertText', false, text);
  };

  _onComposerKeydown = event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this._sendMessage();
    }
  };

  _onComposerClick = event => {
    const remove = event.target.closest('[data-remove-context]');
    if (!remove) {
      this._rememberComposerSelection();
      return;
    }
    const token = remove.closest('[data-context-token]');
    token?.remove();
    this._composerElement()?.focus();
    this._updateSendButton();
  };

  _openContextPicker = () => {
    if (!this.providers.length) {
      this._setStatus(t('messages.empty.no_context', 'Kein Kontext verfügbar.'), true);
      return;
    }
    this._rememberComposerSelection();
    this.contextPicker = { stage: 'providers', providerKey: '', query: '', loading: false };
    this.contextTargets = [];
  };

  _closeContextPicker = () => {
    this.contextPicker = null;
    this.contextTargets = [];
    this.updateComplete.then(() => this._composerElement()?.focus());
  };

  async _selectProvider(provider) {
    this.contextPicker = { stage: 'targets', providerKey: provider.providerKey, query: '', loading: true };
    this.contextTargets = [];
    await this._searchContextTargets('', provider.providerKey);
    this.updateComplete.then(() => this.querySelector('.ks-message-context-search input')?.focus());
  }

  _onContextQuery = event => {
    const query = event.target.value || '';
    this.contextPicker = { ...this.contextPicker, query };
    clearTimeout(this._contextSearchTimer);
    this._contextSearchTimer = setTimeout(() => this._searchContextTargets(query, this.contextPicker?.providerKey), 250);
  };

  async _searchContextTargets(query, providerKey) {
    if (!providerKey) return;
    this.contextPicker = { ...this.contextPicker, loading: true };
    try {
      const current = ProjectPersistenceService.currentOrderMeta();
      const currentTargetId = providerKey === 'order' ? current.auftragId : current.projektId;
      const currentParam = currentTargetId ? `&currentTargetId=${encodeURIComponent(currentTargetId)}` : '';
      const payload = await fetchMessageJson(`/api/messages/context-providers/${encodeURIComponent(providerKey)}/search?q=${encodeURIComponent(query || '')}${currentParam}`);
      if (this.contextPicker?.providerKey !== providerKey || String(this.contextPicker?.query || '') !== String(query || '')) return;
      this.contextTargets = Array.isArray(payload.targets) ? payload.targets : [];
    } catch (error) {
      this._setStatus(error.message, true);
    } finally {
      if (this.contextPicker?.providerKey === providerKey) this.contextPicker = { ...this.contextPicker, loading: false };
    }
  }

  _insertContextToken(target) {
    const editor = this._composerElement();
    if (!editor || !this.contextPicker?.providerKey) return;
    const existing = this._composerParts().filter(part => part.type === 'context');
    const providerKey = this.contextPicker.providerKey;
    if (existing.length >= MAX_CONTEXTS) {
      this._setStatus(t('messages.error.context_limit', 'Es können höchstens 10 Kontexte eingefügt werden.'), true);
      return;
    }
    if (existing.some(part => part.providerKey === providerKey && String(part.targetId) === String(target.targetId))) {
      this._setStatus(t('messages.error.context_duplicate', 'Dieser Kontext ist bereits eingefügt.'), true);
      return;
    }
    const token = document.createElement('span');
    token.className = 'ks-message-composer-token';
    token.dataset.contextToken = 'true';
    token.dataset.providerKey = providerKey;
    token.dataset.targetId = String(target.targetId);
    token.contentEditable = 'false';
    token.tabIndex = 0;
    token.setAttribute('role', 'group');
    token.setAttribute('aria-label', `${target.primaryLabel || ''} ${target.secondaryLabel || ''}`.trim());
    const label = document.createElement('span');
    label.textContent = `${contextIcon(target.iconKey)} ${target.primaryLabel || ''}${target.secondaryLabel ? ` – ${target.secondaryLabel}` : ''}`;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.dataset.removeContext = 'true';
    remove.tabIndex = 0;
    remove.setAttribute('aria-label', t('messages.action.remove_context', 'Kontext entfernen'));
    remove.textContent = '×';
    token.append(label, remove);

    let range = this._composerRange;
    if (!range || !editor.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    range.deleteContents();
    range.insertNode(token);
    const trailing = document.createTextNode('\u00A0');
    token.after(trailing);
    range.setStartAfter(trailing);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    this._composerRange = range.cloneRange();
    this._closeContextPicker();
    this._setStatus('', false);
    this.updateComplete.then(() => {
      editor.focus();
      this._updateSendButton();
    });
  }

  async _sendMessage() {
    if (this.sending || !this.activeRecipient) return;
    const parts = this._composerParts();
    if (!parts.some(part => part.type === 'context' || String(part.text || '').trim())) return;
    this.sending = true;
    this._updateSendButton();
    try {
      const payload = await fetchMessageJson('/api/messages/send', {
        method: 'POST',
        body: JSON.stringify({
          recipientUserId: this.activeRecipient.userId,
          clientMessageId: crypto.randomUUID(),
          parts
        })
      });
      const sent = payload.message;
      this.activeConversationId = Number(sent?.conversationId || this.activeConversationId || 0) || null;
      if (sent && !this.messages.some(message => messageId(message) === messageId(sent))) {
        this.messages = [...this.messages, sent].sort((a, b) => messageId(a) - messageId(b));
      }
      const editor = this._composerElement();
      if (editor) editor.replaceChildren();
      this.composerHasContent = false;
      this._composerRange = null;
      await this._loadConversations();
      await this.updateComplete;
      this._scrollToLatest();
      editor?.focus();
      this._setStatus('', false);
    } catch (error) {
      this._setStatus(error.message || t('messages.error.send', 'Nachricht konnte nicht gesendet werden.'), true);
    } finally {
      this.sending = false;
      this._updateSendButton();
    }
  }

  async _hideMessage(message) {
    const confirmed = await openConfirmDialog({
      title: t('messages.delete.message_title', 'Nachricht für mich löschen'),
      message: t('messages.delete.message_text', 'Diese Nachricht wird nur in deiner Ansicht entfernt.'),
      confirmLabel: t('messages.action.delete', 'Löschen'),
      cancelLabel: t('modal.gemeinsam.button.abbrechen', 'Abbrechen'),
      danger: true
    });
    if (!confirmed) return;
    try {
      await fetchMessageJson(`/api/messages/${messageId(message)}/for-me`, { method: 'DELETE' });
      this.messages = this.messages.filter(item => messageId(item) !== messageId(message));
      await this._loadConversations();
      await syncUnreadCount();
    } catch (error) {
      this._setStatus(error.message, true);
    }
  }

  async _clearConversation() {
    if (!this.activeConversationId) return;
    const confirmed = await openConfirmDialog({
      title: t('messages.delete.conversation_title', 'Chatverlauf für mich löschen'),
      message: t('messages.delete.conversation_text', 'Der Chatverlauf wird nur für dich entfernt. Der andere Benutzer behält seine Nachrichten.'),
      confirmLabel: t('messages.action.delete', 'Löschen'),
      cancelLabel: t('modal.gemeinsam.button.abbrechen', 'Abbrechen'),
      danger: true
    });
    if (!confirmed) return;
    try {
      await fetchMessageJson(`/api/messages/conversations/${this.activeConversationId}/for-me`, { method: 'DELETE' });
      this.messages = [];
      this.activeConversationId = null;
      this.activeRecipient = null;
      this.mobileConversationOpen = false;
      await this._loadConversations();
      await syncUnreadCount();
    } catch (error) {
      this._setStatus(error.message, true);
    }
  }

  async _openContext(part) {
    try {
      const payload = await fetchMessageJson(`/api/messages/context-parts/${part.partId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({})
      });
      if (!payload.available) throw new Error(t('messages.context.unavailable', 'Kontext nicht verfügbar.'));
      if (!payload.accessible || !payload.navigation) throw new Error(t('messages.context.no_access', 'Kein Zugriff auf diesen Kontext.'));
      const navigation = payload.navigation;
      if (!navigation.projectNumber || !navigation.orderNumber) throw new Error(t('messages.context.no_order', 'Für dieses Projekt ist kein zugänglicher Auftrag vorhanden.'));
      const loaded = await ProjectPersistenceService.loadOrderWithUnsavedGuard(navigation.projectNumber, navigation.orderNumber);
      if (!loaded) return;
      this.dispatchEvent(new CustomEvent('message-context-navigated', { bubbles: true, composed: true }));
    } catch (error) {
      this._setStatus(error.message || t('messages.context.unavailable', 'Kontext nicht verfügbar.'), true);
    }
  }

  _backToContacts = () => {
    this.mobileConversationOpen = false;
  };

  _renderRecipientSearch() {
    if (!this.recipientSearchOpen) return nothing;
    return html`
      <div class="ks-message-picker-backdrop">
        <section class="ks-message-picker ks-message-recipient-search" role="dialog" aria-modal="true" aria-label=${t('messages.action.new', 'Neue Nachricht')}>
          <header><strong>${t('messages.action.new', 'Neue Nachricht')}</strong><button class="btn" type="button" @click=${this._closeRecipientSearch}>×</button></header>
          <label>${t('messages.recipient.search', 'Empfänger suchen')}<input type="search" .value=${this.recipientQuery} @input=${this._onRecipientQuery} autocomplete="off"></label>
          <div class="ks-message-picker-results">
            ${this.recipients.length ? this.recipients.map(recipient => html`
              <button type="button" class="ks-message-recipient-result" @click=${() => this._selectRecipient(recipient)}>
                <span class="ks-message-contact-avatar">${String(recipient.displayName || '?').trim().charAt(0).toUpperCase()}</span>
                <span><strong>${recipient.displayName}</strong><small>${recipient.company || (recipient.userType === 'external' ? t('messages.user.external', 'Extern') : t('messages.user.internal', 'Intern'))}</small></span>
              </button>
            `) : html`<div class="empty">${t('messages.empty.no_recipients', 'Keine passenden Empfänger gefunden.')}</div>`}
          </div>
        </section>
      </div>
    `;
  }

  _renderContextPicker() {
    if (!this.contextPicker) return nothing;
    const provider = this.providers.find(item => item.providerKey === this.contextPicker.providerKey);
    const current = ProjectPersistenceService.currentOrderMeta();
    const currentTargetId = provider?.providerKey === 'order' ? current.auftragId : current.projektId;
    const sortedTargets = [...this.contextTargets].sort((a, b) => {
      if (String(a.targetId) === String(currentTargetId)) return -1;
      if (String(b.targetId) === String(currentTargetId)) return 1;
      return 0;
    });
    return html`
      <div class="ks-message-picker-backdrop">
        <section class="ks-message-picker" role="dialog" aria-modal="true" aria-label=${t('messages.action.add_context', 'Kontext hinzufügen')}>
          <header>
            ${this.contextPicker.stage === 'targets' ? html`<button class="btn" type="button" @click=${() => { this.contextPicker = { stage: 'providers', providerKey: '', query: '', loading: false }; this.contextTargets = []; }}>←</button>` : nothing}
            <strong>${provider ? t(provider.labelI18nKey, provider.providerKey) : t('messages.context.available', 'Verfügbare Kontexte')}</strong>
            <button class="btn" type="button" @click=${this._closeContextPicker}>×</button>
          </header>
          ${this.contextPicker.stage === 'providers' ? html`
            <div class="ks-message-provider-list">
              ${this.providers.map(item => html`
                <button type="button" @click=${() => this._selectProvider(item)}>
                  <span>${contextIcon(item.iconKey)}</span><strong>${t(item.labelI18nKey, item.providerKey)}</strong><span>›</span>
                </button>
              `)}
            </div>
          ` : html`
            <label class="ks-message-context-search">${t('messages.context.search', 'Kontext suchen')}<input type="search" .value=${this.contextPicker.query || ''} @input=${this._onContextQuery} autocomplete="off"></label>
            ${!currentTargetId ? html`<div class="small-text">${provider?.providerKey === 'order' ? t('messages.context.save_order_first', 'Der Auftrag muss zuerst gespeichert werden.') : t('messages.context.save_project_first', 'Das Projekt muss zuerst gespeichert werden.')}</div>` : nothing}
            <div class="ks-message-picker-results">
              ${this.contextPicker.loading ? html`<div class="empty">${t('messages.state.loading', 'Wird geladen…')}</div>` : sortedTargets.length ? sortedTargets.map(target => html`
                <button type="button" class="ks-message-context-result" @click=${() => this._insertContextToken(target)}>
                  <span>${contextIcon(target.iconKey)}</span>
                  <span><strong>${target.primaryLabel}</strong><small>${target.secondaryLabel || ''}${String(target.targetId) === String(currentTargetId) ? ` · ${t('messages.context.current', 'Aktuell geöffnet')}` : ''}</small></span>
                </button>
              `) : html`<div class="empty">${t('messages.empty.no_context', 'Kein Kontext verfügbar.')}</div>`}
            </div>
          `}
        </section>
      </div>
    `;
  }

  _renderConversationList() {
    return html`
      <aside class="ks-message-contacts${this.mobileConversationOpen ? ' is-mobile-hidden' : ''}">
        <div class="ks-message-contacts-header">
          <div><strong>${t('messages.title', 'Nachrichten')}</strong><small>${this.connected ? t('messages.state.connected', 'Verbunden') : t('messages.state.offline', 'Verbindung wird hergestellt…')}</small></div>
          <button class="btn primary" type="button" @click=${this._openRecipientSearch}>＋ ${t('messages.action.new', 'Neue Nachricht')}</button>
        </div>
        <div class="ks-message-contact-list" role="list">
          ${this.conversations.length ? this.conversations.map(conversation => {
            const other = conversation.otherUser || {};
            const unread = Number(conversation.unreadCount) || 0;
            return html`
              <button
                type="button"
                role="listitem"
                class="ks-message-contact${conversationId(conversation) === Number(this.activeConversationId) ? ' is-active' : ''}"
                @click=${() => this._selectConversation(conversation)}
              >
                <span class="ks-message-contact-avatar">${String(other.displayName || '?').trim().charAt(0).toUpperCase()}</span>
                <span class="ks-message-contact-copy"><strong>${other.displayName || t('messages.user.unknown', 'Unbekannter Benutzer')}</strong><small>${conversation.preview || ''}</small></span>
                <span class="ks-message-contact-meta"><time>${formatMessageTime(conversation.lastMessageAt)}</time>${unread ? html`<span class="ks-message-unread">${unread > 99 ? '99+' : unread}</span>` : nothing}</span>
              </button>
            `;
          }) : html`<div class="empty">${t('messages.empty.no_conversations', 'Noch keine Unterhaltungen vorhanden.')}</div>`}
        </div>
      </aside>
    `;
  }

  _renderMessagePart(part) {
    if (part.type === 'context') {
      return html`
        <button class="ks-message-context-chip" type="button" @click=${() => this._openContext(part)} aria-label=${`${part.primaryLabel || ''} ${part.secondaryLabel || ''}`.trim()}>
          <span aria-hidden="true">${contextIcon(part.iconKey)}</span>
          <span><strong>${part.primaryLabel || t('messages.context.unavailable', 'Kontext nicht verfügbar')}</strong>${part.secondaryLabel ? html`<small>${part.secondaryLabel}</small>` : nothing}</span>
        </button>
      `;
    }
    if (part.type === 'link') {
      return html`
        <a class="ks-message-context-chip ks-message-link-chip" href=${part.linkUrl} target="_blank" rel="noopener">
          <span aria-hidden="true">⬇</span>
          <span><strong>${part.linkLabel || t('messages.link.download', 'Herunterladen')}</strong></span>
        </a>
      `;
    }
    return html`<span class="ks-message-text-part">${part.text || ''}</span>`;
  }

  _renderThread() {
    if (!this.activeRecipient) {
      return html`
        <section class="ks-message-chat ks-message-chat--empty">
          <div class="empty"><strong>${t('messages.empty.select_conversation', 'Unterhaltung auswählen')}</strong><span>${t('messages.empty.select_hint', 'Wähle links einen Kontakt oder starte eine neue Nachricht.')}</span></div>
        </section>
      `;
    }
    return html`
      <section class="ks-message-chat${this.mobileConversationOpen ? ' is-mobile-open' : ''}">
        <header class="ks-message-chat-header">
          <button class="btn ks-message-mobile-back" type="button" @click=${this._backToContacts} aria-label=${t('messages.action.back', 'Zurück')}>←</button>
          <span class="ks-message-contact-avatar">${String(this.activeRecipient.displayName || '?').trim().charAt(0).toUpperCase()}</span>
          <span><strong>${this.activeRecipient.displayName || ''}</strong><small>${this.activeRecipient.company || (this.activeRecipient.inboxEnabled === false ? t('messages.recipient.disabled', 'Dieser Benutzer kann derzeit keine Nachrichten empfangen') : '')}</small></span>
          ${this.activeConversationId ? html`<button class="btn danger" type="button" @click=${this._clearConversation}>${t('messages.action.clear', 'Verlauf für mich löschen')}</button>` : nothing}
        </header>
        <div class="ks-message-thread" @scroll=${this._scheduleOpenedCheck}>
          ${this.hasMore ? html`<button class="btn ks-message-load-older" type="button" ?disabled=${this.loadingOlder} @click=${this._loadOlder}>${t('messages.action.load_older', 'Ältere Nachrichten laden')}</button>` : nothing}
          ${this.messages.length ? this.messages.map(message => html`
            <article class="ks-message-row${message.own ? ' is-own' : ' is-received'}" data-message-id=${messageId(message)}>
              <div class="ks-message-bubble">
                <div class="ks-message-parts">${(message.parts || []).map(part => this._renderMessagePart(part))}</div>
                <footer><time>${formatMessageTime(message.createdAt)}</time>${message.own && message.openedByRecipient ? html`<span class="ks-message-opened" title=${t('messages.state.opened', 'Geöffnet')}>✓</span>` : nothing}<button type="button" class="ks-message-delete" @click=${() => this._hideMessage(message)} aria-label=${t('messages.action.delete_for_me', 'Nachricht für mich löschen')}>×</button></footer>
              </div>
            </article>
          `) : html`<div class="empty">${t('messages.empty.no_messages', 'Keine Nachrichten vorhanden.')}</div>`}
        </div>
        <div class="ks-message-composer">
          <div
            class="ks-message-composer-editor"
            contenteditable=${this.activeRecipient.inboxEnabled === false ? 'false' : 'true'}
            role="textbox"
            aria-multiline="true"
            aria-label=${t('messages.composer.label', 'Nachricht schreiben')}
            data-placeholder=${this.activeRecipient.inboxEnabled === false ? t('messages.recipient.disabled', 'Dieser Benutzer kann derzeit keine Nachrichten empfangen') : t('messages.composer.placeholder', 'Nachricht schreiben…')}
            @input=${this._rememberComposerSelection}
            @keyup=${this._rememberComposerSelection}
            @mouseup=${this._rememberComposerSelection}
            @click=${this._onComposerClick}
            @keydown=${this._onComposerKeydown}
            @paste=${this._onComposerPaste}
          ></div>
          <div class="ks-message-composer-actions">
            <button class="btn" type="button" ?disabled=${this.activeRecipient.inboxEnabled === false} @click=${this._openContextPicker}>⌁ ${t('messages.action.add_context', 'Kontext hinzufügen')}</button>
            <span class="small-text">${t('messages.composer.keyboard', 'Enter sendet · Shift + Enter für Zeilenumbruch')}</span>
            <button class="btn primary ks-message-send" type="button" ?disabled=${this.sending || !this.activeRecipient || this.activeRecipient.inboxEnabled === false || !this.composerHasContent} @click=${this._sendMessage}>${this.sending ? t('messages.state.sending', 'Wird gesendet…') : t('messages.action.send', 'Senden')}</button>
          </div>
        </div>
      </section>
    `;
  }

  render() {
    return html`
      <div class="ks-message-center" style=${this._styleVariables()}>
        <div class="ks-message-status${this.statusError ? ' is-error' : ''}" aria-live="polite">${this.statusMessage}</div>
        <div class="ks-message-layout">
          ${this._renderConversationList()}
          ${this._renderThread()}
        </div>
        ${this._renderRecipientSearch()}
        ${this._renderContextPicker()}
      </div>
    `;
  }
}

if (!customElements.get('ks-message-center')) customElements.define('ks-message-center', KsMessageCenter);

export { KsMessageCenter };
